import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * @typedef {any} SimpleModal
 * @property {void} open - Open the modal.
 * @property {void} close - Close the modal.
 */

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  connectedCallback() {
    super.connectedCallback();

    /** @type {Element | null} */
    const advisorSearchLink = this.querySelector('[data-advisor-search-link]');
    if (advisorSearchLink) {
      advisorSearchLink.addEventListener('click', this.#openAdvisorSearchPopup);
    }

    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();

    if (this.refs.details.hasAttribute('open')) {
      this.open();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Sets the header group scroll offset
   * @param {number} offset
   */
  #setHeaderGroupScrollOffset(offset = 0) {
    this.style.setProperty('--scroll-offset', `${offset}px`);
  }

  #openAdvisorSearchPopup = () => {
    // Release the focus trap set when the drawer opened,
    // so the modal's input fields are fully interactive.
    removeTrapFocus();

    // Dismiss the drawer before opening the modal.
    this.close();

    /** @type {SimpleModal | null} */
    const advisorSearchPopup = document.querySelector('#consultant-modal');

    if (advisorSearchPopup) {
      // Set initial view to selector (mirrors ConsultantBanner.tsx behaviour)
      // so initializeConsultantSelector opens on the search view, not the profile view.
      advisorSearchPopup.setAttribute('data-initial-view', 'selector');
      advisorSearchPopup.open();
    }
  };

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);

    const summary = details.querySelector('summary');
    if (!summary) return;

    this.#setHeaderGroupScrollOffset(window.scrollY);
    summary.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => details.classList.add('menu-open'));

    trapFocus(details);
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(reset);
      } else {
        trapFocus(this.refs.details);
      }
    });
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    /**
     * @param {AnimationEvent} event
     */
    function removeWillChangeOnAnimationEnd(event) {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    }
    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}

document.addEventListener('DOMContentLoaded', function() {
  /** @type {Array<{label: string, value: string}>} */
  const eventTypes = window?.eventsSettings?.types || [];

  if (eventTypes.length > 0) {
    // Get all event filter option containers
    const filterContainers = document.querySelectorAll('.event-filter-types');

    filterContainers.forEach(container => {
      // Clear existing checkboxes
      container.innerHTML = '';

      // Add new checkboxes from metaobject
      eventTypes.forEach((eventType) => {
        const label = document.createElement('label');
        label.className = 'event-filter-checkbox';

        const input = document.createElement('input');
        input.type = 'checkbox';
        input.name = 'event-type';
        input.value = eventType.value;

        const span = document.createElement('span');
        span.className = 'event-filter-checkbox-text';
        span.textContent = eventType.label;

        label.appendChild(input);
        label.appendChild(span);
        container.appendChild(label);
      });
    });
  }
});
